import { action, observable, makeObservable } from "mobx";
import {Check} from "@sparkware/app-utils";
import {AreaType} from "@sparkware/uc-sdk-core";
import {ServiceBase} from "../ServiceBase";
import {IUserMenuService} from "./user-menu.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../common/const/casinofied-analytics-consts";
import {IOverlayConsumer} from "../overlay/overlay-consumer.interface";
import {IMenuItemRenderer} from "../menu-items-common/renderer/menu-item.renderer.interface";
import {MenuItemKey} from "../configurations/config-data-sections/generated-config-const";
import {GenericUsefulLinksMenuItemRenderer} from "./menu-items/useful-links/generic-useful-links-menu-item-renderer";
import {GenericExpandableUsefulLinksMenuItemRenderer} from "./menu-items/useful-links/generic-expandable-useful-links-menu-item-renderer";
import {
  loadCustomMenuItemsRenderers
} from "../menu-items-common/renderer/dynamic-custom-menu-item-renderers-loader";
import {
  GenericUserUserMenuItemRenderer
} from "./menu-items/user-menu-items/generic-user-user-menu-item-renderer";
import {
  OrbitUserFlowAnalyticsAction,
  OrbitUserFlowAnalyticsCategory,
  OrbitUserFlowAnalyticsLabel
} from "../analytics/analytics.service.interface";
import {UserMenuItemConfigReader} from "./menu-items/user-menu-item-config-reader";
import {ExpandableMenuItemConfig} from "../menu-items-common/expandable/expandable-menu-item.interface";


export class UserMenuService extends ServiceBase implements IUserMenuService, IOverlayConsumer {
  @observable.ref
  isOpen: boolean = false;
  @observable.ref
  isRewardsInfo: boolean = false;

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    this.services.overlay.registerConsumer(this);
    this.services.sdk.events.onGameOpen(() => this.services.userMenu.closeMenu());

    this._menuItemConfigReader = new UserMenuItemConfigReader(services);

    let rendererModules = require.context('./menu-items/useful-links/custom-renderers', true, /.+\.renderer\.(ts|tsx)$/);
    this._customUsefulLinksMenuItems = loadCustomMenuItemsRenderers(rendererModules.keys().map(rendererModules),
                                                                this._menuItemConfigReader,
                                                                this.services);
    /*
    //Uncomment this when we will have custom renderers for user menu items
    rendererModules = require.context('./menu-items/user-menu-items/custom-renderers', true, /.+\.renderer\.(ts|tsx)$/);
    this._customUserMenuItems = loadCustomMenuItemsRenderers(rendererModules.keys().map(rendererModules),
        this._userMenuItemConfigReader,
        this.services);

     */
  }

  readonly _menuItemConfigReader: UserMenuItemConfigReader;
  private readonly _customUsefulLinksMenuItems: Partial<Record<MenuItemKey, IMenuItemRenderer>> = {};
  private readonly _customUserMenuItems: Partial<Record<MenuItemKey, IMenuItemRenderer>> = {};


  get needsOverlay(): boolean {
    return this.isOpen;
  }

  getOverlayZIndex(): number | undefined {
    return this.zIndex - 1;
  }

  get zIndex(): number {
    return this.services.sideMenu.zIndex - 1;
  }

  onOverlayClicked(): void {
    this.closeMenu();
  }

  @action.bound
  private _setIsOpen(shouldOpen: boolean) {
    this.isOpen = shouldOpen;
  }

  @action.bound
  private _setIsRewardsInfo(shouldBeRewards: boolean) {
    this.isRewardsInfo = shouldBeRewards;
  }

  private _sendOrbitUserFlowAnalytics(action: OrbitUserFlowAnalyticsAction): void {
    this.services.analytics.sendOrbitUserFlowEvent(OrbitUserFlowAnalyticsCategory.Navigation, action, OrbitUserFlowAnalyticsLabel.UserArea);
  }

  private menuKeyIsExpandableItem(key: MenuItemKey | ExpandableMenuItemConfig): key is ExpandableMenuItemConfig {
    if (Check.isObject(key) && !Check.isNullOrUndefined(key.menuItemKey) && Check.isArray(key.subItems)) {
      return true;
    }
    return false;
  }

  private _getUsefulLinksMenuItem(key: MenuItemKey | ExpandableMenuItemConfig): IMenuItemRenderer {
    if (this.menuKeyIsExpandableItem(key)) {
      return new GenericExpandableUsefulLinksMenuItemRenderer(key, this._menuItemConfigReader, this.services);
    }
    return this.getUsefulLinkMenuItem(key);
  }

  public getUsefulLinkMenuItem(key: MenuItemKey): IMenuItemRenderer {
    let customMenuItem = this.services.regulation.current.menuItems.getUsefulLinksMenuItem(key);
    if(customMenuItem) {
      return customMenuItem;
    }
    customMenuItem = this._customUsefulLinksMenuItems[key];
    if(customMenuItem) {
      return customMenuItem;
    }
    return new GenericUsefulLinksMenuItemRenderer(key, this._menuItemConfigReader, this.services);
  }

  private _getUserMenuItem(key: MenuItemKey): IMenuItemRenderer {
    let menuItem = this.services.regulation.current.menuItems.getUserMenuItem(key);
    if(menuItem) {
      return menuItem;
    }
    menuItem = this._customUserMenuItems[key];
    if(menuItem) {
      return menuItem;
    }
    menuItem = new GenericUserUserMenuItemRenderer(key, this._menuItemConfigReader, this.services);
    return menuItem;
  }

  get usefulLinksMenuItems(): IMenuItemRenderer[] {
    return (this.services.configuration.data.drawer.usefulLinks ?? []).map(key => this._getUsefulLinksMenuItem(key)).filter(menuItem => menuItem.display);
  }

  get userMenuItems(): IMenuItemRenderer[] {
    return (this.services.configuration.data.drawer.userMenuItems ?? []).map(key => this._getUserMenuItem(key)).filter(menuItem => menuItem.display);
  }

  doLogout() {
    this._sendOrbitUserFlowAnalytics('logoutMenuItem');
    this.services.regulation.current.logoutUser();
    this.closeMenu();
  }

  /**
   * This method returns a greeting translation key based on time of day. "Good Morning", "Good Afternoon" etc
   * It was used in WelcomeUser component but not anymore, however, I did not deleted this method
   */
  get greeting() {
    const now = new Date();
    const hour = now.getHours();
    const minute = now.getMinutes();

    const {defaultMessage, conditions} = this.services.configuration.data.drawer.welcomeMessage;
    let result = defaultMessage;
    for (const rule of conditions) {
      const [ruleHour, ruleMinute] = rule.time.split(':').map(parseInt);

      if (hour > ruleHour) {
        result = rule.text;
      } else if (hour === ruleHour) {
        // NB: only if the current hour is strictly equal to the rule hour do we need to check the minutes
        if (minute >= ruleMinute) {
          result = rule.text;
        }
      }
    }

    return result;
  }

  launchMyAccount(): void {
    this._sendOrbitUserFlowAnalytics('myAccountButton');
    this.services.externalLinks.openMyAccount();
    this.closeMenu();
  }

  launchDeposit(): void {
    this._sendOrbitUserFlowAnalytics('depositButton');
    this.services.cashier.openDeposit({
      channel: {
        source: CHANNEL_SOURCE.casinoClient,
        area: AreaType.UserArea,
        element: CHANNEL_ELEMENTS.depositButton
      }
    });
    this.closeMenu();
  }

  launchWithdraw(): void {
    this._sendOrbitUserFlowAnalytics('withdrawButton');
    this.services.cashier.openWithdraw({
      channel: {
        source: CHANNEL_SOURCE.casinoClient,
        area: AreaType.UserArea,
        element: CHANNEL_ELEMENTS.withdrawButton
      }
    });
    this.closeMenu();
  }

  openRewards(): void {
    this.openMenu();
    this._setIsRewardsInfo(true);
  }

  closeMenu(): void {
    if (this.isOpen) {
      this._setIsOpen(false);
      this._setIsRewardsInfo(false);
    }
  }

  openMenu(): void {
    if (!this.isOpen) {
      this.services.analytics.sendOrbitUserFlowEvent('Navigator', 'Navbar', 'Open_UserMenu');
      this._setIsOpen(true);
      this.services.sideMenu.closeMobileMenu();
      this.services.drawer.right.closeDrawer();
    }
  }
}


